import * as React from 'react';

import CheckBox from '@/components/elements/Form/CheckBox';
import { useUserContext } from '@/contexts/user';
import { CreditCardMemberStatus } from '@/lib/apollo/types/gen/graphql';
import { creditCardSelector } from '@/selectors/creditCardBrand';

import {
  CheckBoxContainer,
  CheckboxWrapper,
  Container,
  CreditDormantLink,
  CreditDormantNote,
  CreditDormantTitle,
  CreditDormantWrapper,
  CreditNotice,
  ExternalLink,
  Icon,
  Notice,
  Title,
} from './style';

interface AutoChargeSelectProps {
  insufficientBalance: number;
  isAutoChargeByCash: boolean;
  setIsAutoChargeByCash: React.Dispatch<React.SetStateAction<boolean>>;
  isAutoChargeByCredit: boolean;
  setIsAutoChargeByCredit: React.Dispatch<React.SetStateAction<boolean>>;
  creditCardStatus: CreditCardMemberStatus;
}

const AutoChargeSelect: React.FC<AutoChargeSelectProps> = ({
  insufficientBalance,
  isAutoChargeByCash,
  setIsAutoChargeByCash,
  isAutoChargeByCredit,
  setIsAutoChargeByCredit,
  creditCardStatus,
}) => {
  const { user } = useUserContext();
  const { isAvailable, isPcc } = creditCardSelector(user.creditCard);

  return (
    <Container>
      <Icon />
      <Title>
        toreta+ 残高が
        <span data-testid="insufficient-balance">
          {insufficientBalance.toLocaleString()}円分
        </span>
        <br />
        不足しています
      </Title>
      <CheckBoxContainer>
        <CheckboxWrapper>
          <CheckBox
            htmlFor="cash"
            checked={isAutoChargeByCash}
            onChange={e => setIsAutoChargeByCash(e.target.checked)}
            disabled={insufficientBalance > user.wallet!.balance}
          >
            入金/払戻金 残高からチャージする
          </CheckBox>
        </CheckboxWrapper>
        {creditCardStatus === CreditCardMemberStatus.Dormant ? (
          <CreditDormantWrapper>
            <CreditDormantTitle>
              ただいまクレジットカードを利用することができません
            </CreditDormantTitle>
            <CreditDormantNote>
              以下リンクより、登録しているクレジットカード情報をご確認ください。
            </CreditDormantNote>
            <CreditDormantLink>
              <a href={import.meta.env.VITE_DMM_PAYMENT_URL}>
                クレジットカード情報を確認する
              </a>
            </CreditDormantLink>
          </CreditDormantWrapper>
        ) : (
          <CheckboxWrapper>
            {isAvailable && isPcc && (
              <CheckBox
                htmlFor="credit"
                checked={isAutoChargeByCredit}
                onChange={e => setIsAutoChargeByCredit(e.target.checked)}
              >
                クレジットカードでチャージする
              </CheckBox>
            )}
            {isAvailable && !isPcc && (
              <CheckBox
                htmlFor="credit"
                checked={false}
                disabled={true} // ちょコム停止により永久非活性
                onChange={e => setIsAutoChargeByCredit(e.target.checked)}
              >
                クレジットカードでチャージする
              </CheckBox>
            )}
            {isAvailable && !isPcc && (
              <Notice>
                <ExternalLink
                  href={`${
                    import.meta.env.VITE_DMM_NAV_BASE_URL
                  }/news/kl3j480j_mh`}
                  target="_blank"
                >
                  【重要】クレジットカードメンテナンスによるVISAカード利用の一時停止について
                </ExternalLink>
              </Notice>
            )}
            {isAvailable && (
              <Notice>
                ※不足金額が1,000円未満の場合は一律で1,000円チャージされます。
              </Notice>
            )}
            {!isAvailable && (
              <CreditNotice>
                <p>
                  クレジットカードを登録しておくといつでも手軽にチャージできます。
                  <br />
                  <div>
                    <a
                      href={import.meta.env.VITE_DMM_PAYMENT_URL}
                      target="_blank"
                    >
                      クレジットカードの登録はこちら
                    </a>
                  </div>
                  <span>
                    利用可能なクレジットカードは
                    <a
                      href="https://support.dmm.com/payment/article/46418"
                      target="_blank"
                    >
                      こちら
                    </a>
                    から確認してください。
                  </span>
                </p>
              </CreditNotice>
            )}
          </CheckboxWrapper>
        )}
      </CheckBoxContainer>
    </Container>
  );
};
export default AutoChargeSelect;
